import { useCallback, useEffect, useState } from 'react'
import { Loader2, Plus, Search, Trash2, Wand2 } from 'lucide-react'
import { opcoApi } from '../../../../services/api'
import { useToast } from '../../../../context/ToastContext'
import Toggle from '../../../Toggle'
import { ConfirmDialog, PanelState, asList, cardCls, errorMessage, inputCls, primaryBtn, secondaryBtn } from '../shared'
import { outcomeLabel, ruleErrorMessage } from './helpers'

// The rules: "people who sign in with @example.nl belong to NL01". Each rule has its own switch,
// OFF when created. The switch only decides what happens to people who SIGN IN from now on;
// people who are already here are placed with "Place existing people" below.
export default function RulesPanel({ opcos }) {
  const toast = useToast()
  const activeOpcos = (opcos || []).filter((o) => o.is_active !== false)
  const [rules, setRules] = useState([])
  const [status, setStatus] = useState([])
  const [adopting, setAdopting] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [matchType, setMatchType] = useState('domain')
  const [value, setValue] = useState('')
  const [opcoId, setOpcoId] = useState('')
  const [subdomains, setSubdomains] = useState(false)
  const [formError, setFormError] = useState(null)
  const [saving, setSaving] = useState(false)

  const [enabling, setEnabling] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [busy, setBusy] = useState(false)

  const [testEmail, setTestEmail] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await opcoApi.listAssignRules()
      setRules(asList(res?.data, 'rules'))
      // Optional: the per-OpCo domain summary must never hide the rules.
      opcoApi.assignDomainStatus()
        .then((r) => setStatus(asList(r?.data, 'opcos')))
        .catch(() => setStatus([]))
    } catch (err) {
      setError(errorMessage(err, 'Failed to load the rules'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const add = async (e) => {
    e.preventDefault()
    setFormError(null)
    if (!value.trim()) {
      setFormError(matchType === 'domain' ? 'Enter a domain, for example example.nl' : 'Enter a full email address')
      return
    }
    if (!opcoId) { setFormError('Choose the OpCo these people belong to'); return }
    setSaving(true)
    try {
      await opcoApi.createAssignRule({
        match_type: matchType,
        value: value.trim(),
        opco_id: Number(opcoId),
        include_subdomains: matchType === 'domain' && subdomains,
      })
      toast.success('Rule added', 'It is switched off. Switch it on when you are ready.')
      setValue('')
      setSubdomains(false)
      await load()
    } catch (err) {
      setFormError(ruleErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const setEnabled = async (rule, enabled) => {
    setBusy(true)
    try {
      await opcoApi.updateAssignRule(rule.id, { is_enabled: enabled })
      toast.success(
        enabled ? 'Rule switched on' : 'Rule switched off',
        enabled
          ? `New people with ${rule.match_value} now join ${rule.opco_code} when they first sign in.`
          : `${rule.match_value} no longer places anyone.`,
      )
      setEnabling(null)
      await load()
    } catch (err) {
      toast.error('Could not change the rule', errorMessage(err))
      setEnabling(null)
    } finally {
      setBusy(false)
    }
  }

  const confirmDelete = async () => {
    setBusy(true)
    try {
      await opcoApi.deleteAssignRule(deleting.id)
      toast.success('Rule deleted', `${deleting.match_value} is no longer a rule. Nobody was removed from an OpCo.`)
      setDeleting(null)
      await load()
    } catch (err) {
      toast.error('Could not delete the rule', errorMessage(err))
      setDeleting(null)
    } finally {
      setBusy(false)
    }
  }

  // One click turns a domain the OpCo already claims from its members into a rule.
  const adopt = async (row, domain) => {
    setAdopting(`${row.opco_id}:${domain}`)
    try {
      await opcoApi.createAssignRule({ match_type: 'domain', value: domain, opco_id: row.opco_id, include_subdomains: false })
      toast.success('Rule added', `@${domain} belongs to ${row.code}. It is switched off for sign-in placement.`)
      await load()
    } catch (err) {
      toast.error('Could not add the rule', ruleErrorMessage(err))
    } finally {
      setAdopting(null)
    }
  }

  const needsAttention = status.filter((row) => row.rule_count === 0 || row.inferred_domains?.length > 0)

  const runTest = async (e) => {
    e.preventDefault()
    if (!testEmail.trim()) return
    setTesting(true)
    setTestResult(null)
    try {
      const res = await opcoApi.testAssignEmail(testEmail.trim())
      setTestResult(res?.data || null)
    } catch (err) {
      toast.error('Could not test', errorMessage(err))
    } finally {
      setTesting(false)
    }
  }

  return (
    <section className={cardCls} aria-label="Automatic membership rules">
      <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Rules</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          A rule says that people with an email address like <span className="font-mono">@example.nl</span> belong to one OpCo.
          A switched-on rule adds new people to that OpCo (as plain members) the first time they sign in.
          It never changes people who are already here: use "Place existing people" below for them.
          Super Admins, guests and service accounts are never placed.
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Rules also decide who admins can invite. On or off, a rule keeps its people out of the other OpCos&apos;
          invite search: an admin in one OpCo only finds people of their own OpCo and people no OpCo claims,
          such as a group-wide domain. Super Admins find everyone.
        </p>
      </div>

      {needsAttention.length > 0 && (
        <ul aria-label="OpCo domains" className="space-y-1.5 border-b border-gray-100 dark:border-gray-800 px-5 py-3">
          {needsAttention.map((row) => (
            <li key={row.opco_id} className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <span className="font-mono font-semibold">{row.code}</span>
              {row.rule_count === 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  No domain set
                </span>
              )}
              {(row.inferred_domains || []).map((d) => (
                <span key={d.domain} className="inline-flex items-center gap-1.5">
                  <span>
                    uses <span className="font-mono">@{d.domain}</span> automatically
                    ({d.members} member{d.members === 1 ? '' : 's'})
                  </span>
                  <button
                    type="button"
                    onClick={() => adopt(row, d.domain)}
                    disabled={adopting !== null}
                    className="rounded-md border border-gray-200 dark:border-gray-700 px-2 py-0.5 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    {adopting === `${row.opco_id}:${d.domain}` ? 'Adding...' : 'Add as rule'}
                  </button>
                </span>
              ))}
              {row.rule_count === 0 && !(row.inferred_domains || []).length && (
                <span>its people show in every OpCo&apos;s invite search until a rule is added.</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={add} className="grid gap-2 px-5 py-4 sm:grid-cols-[8rem_minmax(0,1fr)_12rem_auto]" noValidate>
        <select
          aria-label="Rule type"
          value={matchType}
          onChange={(e) => { setMatchType(e.target.value); setSubdomains(false) }}
          className={inputCls}
        >
          <option value="domain">Domain</option>
          <option value="email">One address</option>
        </select>
        <input
          aria-label="Domain or email address"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={matchType === 'domain' ? 'example.nl' : 'orders@example.co.uk'}
          className={inputCls}
        />
        <select aria-label="OpCo" value={opcoId} onChange={(e) => setOpcoId(e.target.value)} className={inputCls}>
          <option value="">Choose OpCo...</option>
          {activeOpcos.map((o) => <option key={o.id} value={o.id}>{o.code} - {o.name}</option>)}
        </select>
        <button type="submit" disabled={saving} className={primaryBtn}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add rule
        </button>
        {matchType === 'domain' && (
          <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 sm:col-span-4">
            <input type="checkbox" checked={subdomains} onChange={(e) => setSubdomains(e.target.checked)} className="mt-0.5" />
            <span>
              Also match sub-domains (for example <span className="font-mono">emea.{value.trim() || 'example.nl'}</span>).
              Only tick this if every sub-domain really belongs to the same company.
            </span>
          </label>
        )}
        {formError && <p role="alert" className="text-xs text-red-600 dark:text-red-400 sm:col-span-4">{formError}</p>}
      </form>

      <PanelState loading={loading} error={error} onRetry={load} empty={rules.length === 0} emptyText="No rules yet.">
        <ul className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
          {rules.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900 dark:text-gray-100">
                  <span className="font-mono">{r.match_type === 'domain' ? `@${r.match_value}` : r.match_value}</span>
                  {r.include_subdomains && <span className="ml-2 text-xs text-gray-500">+ sub-domains</span>}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  joins <span className="font-mono">{r.opco_code}</span> - {r.opco_name}
                  {r.opco_is_active === false && <span className="ml-1 font-semibold text-amber-600">(inactive)</span>}
                </p>
              </div>
              <span className={`text-xs font-semibold ${r.is_enabled ? 'text-green-700 dark:text-green-400' : 'text-gray-500'}`}>
                {r.is_enabled ? 'On' : 'Off'}
              </span>
              <Toggle
                checked={Boolean(r.is_enabled)}
                disabled={busy}
                ariaLabel={`${r.is_enabled ? 'Switch off' : 'Switch on'} the rule for ${r.match_value}`}
                onChange={() => (r.is_enabled ? setEnabled(r, false) : setEnabling(r))}
              />
              <button
                type="button"
                onClick={() => setDeleting(r)}
                aria-label={`Delete the rule for ${r.match_value}`}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </PanelState>

      <form onSubmit={runTest} className="flex flex-wrap items-center gap-2 border-t border-gray-100 dark:border-gray-800 px-5 py-4">
        <input
          type="email"
          aria-label="Email address to test"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="Try an address: anna@example.nl"
          className={`${inputCls} max-w-xs`}
        />
        <button type="submit" disabled={testing || !testEmail.trim()} className={secondaryBtn}>
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Test
        </button>
        {testResult && <p role="status" className="text-sm text-gray-700 dark:text-gray-200">{outcomeLabel(testResult)}</p>}
      </form>

      {enabling && (
        <ConfirmDialog
          title="Switch this rule on?"
          message={`From now on, a new person who signs in with ${enabling.match_type === 'domain' ? '@' : ''}${enabling.match_value} joins ${enabling.opco_code} automatically. People who are already here are not changed; use "Place existing people" for them.`}
          confirmLabel="Switch on"
          busy={busy}
          onConfirm={() => setEnabled(enabling, true)}
          onCancel={() => setEnabling(null)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title="Delete this rule?"
          message={`${deleting.match_value} stops placing anyone. People it already placed stay in ${deleting.opco_code}.`}
          confirmLabel="Delete"
          danger
          busy={busy}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </section>
  )
}
